import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreviewTicket } from '../api/types';
import { apiFetch } from '../api/client';

/**
 * Cửa sổ xem trước học liệu.
 *
 * Bài được nhúng bằng `<iframe>` trỏ vào h5p-core với một vé ký ngắn hạn.
 * Iframe KHÔNG gửi được header `Authorization`, nên đây là cách duy nhất
 * chuyển danh tính sang mà không nhét token thật vào URL.
 *
 * H5P chạy trong iframe LỒNG iframe (h5p-core dựng thêm một lớp nữa) — điều
 * này chỉ quan trọng khi gỡ lỗi: DOM của trang cha không thấy nội dung bài.
 */
export function PreviewDialog({
  title,
  ticket,
  contentId,
  onClose,
}: {
  title: string;
  ticket: PreviewTicket;
  contentId: string;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [expired, setExpired] = useState(false);
  const [url, setUrl] = useState(ticket.url);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  /**
   * Vé sống 5 phút. Hết hạn thì iframe hiện lỗi 401 câm, nên đếm ngược và báo
   * rõ thay vì để người dùng nhìn khung trắng đoán chuyện gì xảy ra.
   */
  useEffect(() => {
    const timer = window.setTimeout(() => setExpired(true), ticket.expiresInSec * 1000);
    return () => window.clearTimeout(timer);
  }, [ticket.expiresInSec]);

  const renew = async (): Promise<void> => {
    const issued = await apiFetch<PreviewTicket>(`/api/admin/content/${contentId}/preview-ticket`, {
      method: 'POST',
      body: {},
    });
    setUrl(issued.url);
    setExpired(false);
  };

  return (
    <dialog ref={dialogRef} className="dialog dialog--wide" onClose={onClose}>
      <header className="dialog__header">
        <h2>{t('preview.heading', { title })}</h2>
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.close')}
        </button>
      </header>

      {expired ? (
        <div className="feedback feedback--error" role="alert">
          <p>{t('preview.expired')}</p>
          <button type="button" onClick={() => void renew()}>
            {t('common:action.retry')}
          </button>
        </div>
      ) : (
        <iframe
          className="preview-frame"
          src={url}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; camera; microphone"
        />
      )}

      <footer className="dialog__footer">
        {/* rel="noreferrer" để vé không rò qua header Referer sang trang khác. */}
        <a href={url} target="_blank" rel="noreferrer">
          {t('preview.openInNewTab')}
        </a>
      </footer>
    </dialog>
  );
}
